import { useState } from 'react';

import { Collapse } from '@mui/material';
import { Icon } from '@sdb/planning-ui';

import {
  Actions,
  Body,
  Card,
  Chevron,
  ChevronButton,
  Header,
  HeaderContent,
  MetaIcon,
  MetaItem,
  MetaRow,
  MetaText,
  StatusBar,
  StatusChip,
  Title,
  TitleIcon,
  TopRow
} from './RuleSetCard.styles';

import type { RuleSetCardProps } from './RuleSetCard.types';

/**
 * A named set of treatment rules — a care receiver's prescription, a protocol — as a card: a status
 * bar, the name with a status chip, a line of facts, and the rule grid as its body. The prescriptions
 * fold the body away; a protocol's detail view shows it straight away.
 */
const RuleSetCard = ({
  title,
  status,
  statusLabel,
  badges,
  actions,
  metaItems,
  children,
  collapsible = false,
  size = 'medium'
}: RuleSetCardProps) => {
  'use memo';
  const [expanded, setExpanded] = useState(false);
  const canToggle = collapsible && !!children;
  const toggle = () => setExpanded((previous) => !previous);

  return (
    <Card variant="outlined">
      <Header clickable={canToggle} onClick={canToggle ? toggle : undefined}>
        <StatusBar status={status} />
        <HeaderContent>
          <TopRow size={size}>
            <TitleIcon size={size}>
              <Icon type="file-lines" fontSize="inherit" />
            </TitleIcon>
            <Title variant="body1" component="h2" size={size}>
              {title}
            </Title>
            {statusLabel && (
              <StatusChip
                label={statusLabel}
                size="small"
                variant="outlined"
                status={status}
              />
            )}
            {badges}
            {actions && (
              // A click on an action is not a click on a collapsible header
              <Actions onClick={(event) => event.stopPropagation()}>
                {actions}
              </Actions>
            )}
          </TopRow>
          <MetaRow>
            {metaItems.map(({ key, icon, content }) => (
              <MetaItem key={key}>
                <MetaIcon size={size}>
                  <Icon type={icon} fontSize="inherit" />
                </MetaIcon>
                <MetaText variant="body2" component="span" size={size}>
                  {content}
                </MetaText>
              </MetaItem>
            ))}
          </MetaRow>
        </HeaderContent>
        {canToggle && (
          <ChevronButton focusRipple aria-expanded={expanded}>
            <Chevron expanded={expanded}>
              <Icon type="chevron-down" fontSize="inherit" />
            </Chevron>
          </ChevronButton>
        )}
      </Header>
      {children &&
        (collapsible ? (
          <Collapse in={expanded} unmountOnExit>
            <Body>{children}</Body>
          </Collapse>
        ) : (
          <Body>{children}</Body>
        ))}
    </Card>
  );
};

export default RuleSetCard;
